import React, { useState, useEffect } from 'react';
import { X, HelpCircle } from 'lucide-react';

// Testi guida per ogni sezione
const GUIDE_TEXTS = {
  gamification: {
    title: "La Tua Barriera Corallina",
    text: "Benvenuto! Completa una sfida al giorno per far crescere la tua barriera corallina. Ogni sfida fa apparire nuovi elementi: coralli colorati, anemoni, pesciolini, una tartaruga e persino uno squalo! ⚠️ Attenzione: se non giochi per 3 giorni i coralli sbiancano e i pesci scappano! 🪸🐠🐢"
  },
  mappa: {
    title: "Mappa Interattiva",
    text: "Esplora la mappa per vedere le segnalazioni SOS, i punti di raccolta e le aree marine protette. Tocca i marker per maggiori dettagli! 🗺️"
  },
  meteo: {
    title: "Meteo Marino",
    text: "Consulta le previsioni meteo marine: onde, vento, temperatura dell'acqua e condizioni per le tue attività in mare! ⛵"
  },
  mareluna: {
    title: "Mare & Luna",
    text: "Scopri le fasi lunari, gli orari delle maree e le mappe termiche del mare. Perfetto per pianificare le tue uscite! 🌙"
  },
  sos: {
    title: "Segnalazioni SOS",
    text: "Hai visto un animale in difficoltà o un'emergenza ambientale? Segnalalo qui con foto e posizione. Il tuo aiuto è prezioso! 🆘"
  },
  rivista: {
    title: "Rivista Marina",
    text: "Articoli, notizie e approfondimenti sul mare e la sua conservazione. Resta aggiornato sulle ultime novità! 📰"
  },
  raccolta: {
    title: "Nuova Raccolta",
    text: "Registra la plastica che raccogli! Fotografa i rifiuti, indica il peso e contribuisci alla pulizia del mare. Ogni grammo conta! ♻️"
  },
  storico: {
    title: "Storico Raccolte",
    text: "Qui trovi tutte le tue raccolte passate. Monitora i tuoi progressi e l'impatto positivo che stai avendo! 📊"
  },
  profilo: {
    title: "Il Tuo Profilo",
    text: "Gestisci le tue informazioni, visualizza le statistiche personali e i badge guadagnati! 👤"
  },
  admin: {
    title: "Pannello Admin",
    text: "Gestisci utenti, visualizza statistiche globali e configura le impostazioni dell'app. Solo per amministratori! ⚙️"
  },
  maredu: {
    title: "MareDu - Scuole",
    text: "Benvenuto nella sezione dedicata alle scuole! Qui trovi i laboratori educativi sul mare e puoi compilare il questionario dopo aver partecipato a un'attività. Il tuo feedback ci aiuta a migliorare! 🏫🌊"
  },
  questionario: {
    title: "Questionario Scuole",
    text: "Ciao! Sono qui per aiutarti a compilare il questionario. Rispondi con sincerità alle domande sul laboratorio a cui hai partecipato. Non ci sono risposte giuste o sbagliate, vogliamo solo sapere cosa ne pensi! 📝✨"
  },
  mare: {
    title: "Fishing for Litter",
    text: "Il programma di raccolta rifiuti con i pescatori. Scopri come partecipare e contribuire alla pulizia del mare! 🎣"
  },
  missione: {
    title: "Missione Mensile",
    text: "Scegli un animale marino e completa 30 sfide per sbloccarlo! Ogni sfida completata rivela un po' di più dell'immagine. 🐢"
  },
  eventi: {
    title: "Eventi",
    text: "Scopri gli eventi di pulizia spiagge, workshop e iniziative nella tua zona. Partecipa e fai la differenza! 📅"
  },
  impostazioni: {
    title: "Impostazioni",
    text: "Personalizza l'app: lingua, notifiche e preferenze. Rendi Respiro Blu perfetta per te! ⚙️"
  }
};

// Componente Emme Guide
const EmmeGuide = ({ sectionId, onClose }) => {
  const guide = GUIDE_TEXTS[sectionId] || {
    title: "Benvenuto!",
    text: "Esplora questa sezione per scoprire tutte le funzionalità! 🌊"
  };

  const [floatOffset, setFloatOffset] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setFloatOffset(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const floatY = Math.sin(floatOffset * Math.PI / 180) * 4;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-sm bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl shadow-2xl border border-cyan-500/30 overflow-hidden animate-slide-up mb-20">
        {/* Header con Emme */}
        <div className="relative bg-gradient-to-r from-cyan-600/20 to-teal-600/20 p-4 flex items-center gap-4">
          {/* Mini Emme animata */}
          <div 
            className="w-16 h-16 flex-shrink-0"
            style={{ transform: `translateY(${floatY}px)` }}
          >
            <img
              src="/emme/happy.png?v3"
              alt="Emme"
              className="w-full h-full object-contain drop-shadow-lg"
              onError={(e) => { e.target.innerHTML = '🐢'; }}
            />
          </div>
          
          <div className="flex-1">
            <h3 className="text-white font-bold text-lg">{guide.title}</h3>
            <p className="text-cyan-400 text-xs">Emme ti spiega</p>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        {/* Contenuto */}
        <div className="p-4">
          <p className="text-cyan-100 text-sm leading-relaxed">{guide.text}</p>
        </div>
        
        {/* Footer */}
        <div className="px-4 pb-4">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold rounded-xl hover:from-cyan-400 hover:to-teal-400 transition-all"
          >
            Ho capito! 👍
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
};

// Hook per gestire la guida (appare ogni 10 visite per sezione)
export const useEmmeGuide = (sectionId) => {
  const [showGuide, setShowGuide] = useState(false);
  const [hasSeenGuide, setHasSeenGuide] = useState(true);

  useEffect(() => {
    // Incrementa contatore visite per questa sezione
    const visitCounts = JSON.parse(localStorage.getItem('emme_section_visits') || '{}');
    const currentCount = (visitCounts[sectionId] || 0) + 1;
    visitCounts[sectionId] = currentCount;
    localStorage.setItem('emme_section_visits', JSON.stringify(visitCounts));
    
    // Mostra ogni 10 visite (1, 11, 21, ...) 
    const shouldShow = currentCount === 1 || currentCount % 10 === 1;
    setHasSeenGuide(!shouldShow);
    
    if (shouldShow) {
      // Piccolo delay per far caricare prima la pagina
      const timer = setTimeout(() => {
        setShowGuide(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [sectionId]);

  const closeGuide = () => {
    setShowGuide(false);
    setHasSeenGuide(true);
  };

  const openGuide = () => {
    setShowGuide(true);
  };

  return { showGuide, closeGuide, openGuide, hasSeenGuide };
};

// Pulsante per richiamare Emme
export const EmmeHelpButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed top-20 right-4 z-30 w-10 h-10 bg-cyan-500/20 hover:bg-cyan-500/30 backdrop-blur-sm rounded-full flex items-center justify-center border border-cyan-500/30 shadow-lg transition-all hover:scale-110"
    title="Chiedi aiuto a Emme"
  >
    <HelpCircle className="w-5 h-5 text-cyan-400" />
  </button>
);

export default EmmeGuide;
